import { Badge, Box, Card, Flex, Heading, Text } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, priceFormatter, useDrawer, useMobileContext } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ordersService from '../../api/services/ordersService';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';
import { useMeta } from '../../api/hooks/useMeta';
import { useGetAllDiscountCodes } from '../../api/hooks/useDiscountCodeSuperadmin';
import { OrderDrawer } from './SuperadminOrderDrawer';

const columnHelper = createColumnHelper<Order>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function Orders() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<Order>(), []);
	const { t } = useTranslation();
	const { data: accounts } = useGetAllAccounts();
	const { isMobile } = useMobileContext();
	const { data: discountCodes } = useGetAllDiscountCodes();
	const { openDrawer } = useDrawer();

	const { meta } = useMeta('ordersService');

	function getPaymentStatusBadgeColor(paymentStatus: PaymentStatus | OrderStatus): string {
		switch (paymentStatus) {
			case 'paid':
				return 'rgb(61, 213, 152)';
			case 'completed':
				return 'rgb(61, 213, 152)';
			case 'draft':
				return 'yellow.400';
			case 'inProgress':
				return 'yellow.400';
			case 'awaitingPayment':
				return 'yellow.400';
			case 'awaitingInvoiceCreation':
				return 'yellow.400';
			case 'cancelled':
				return 'red.400';
			default:
				return 'grey.500';
		}
	}

	function getPaymentMethodTranslation(paymentMethod: PaymentMethod): string {
		return (meta?.paymentMethod?.[paymentMethod] as string) || paymentMethod;
	}

	const columns = [
		columnHelper.accessor('orderID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('accountID', {
			header: t('general.account'),
			enableSorting: false,
			cell: ({ row }) => {
				const accountName = accounts?.data.accounts?.find(account => account.accountID === row.original.accountID)?.name;
				return <Text>{accountName}</Text>;
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('orderRows', {
			header: t('general.product_info'),
			enableSorting: false,
			cell: ({ row }) => {
				return <Text>{row.original.orderRows?.[0]?.name}</Text>;
			},
		}),
		columnHelper.accessor('paymentMethod', {
			header: t('general.payment_method'),
			sortingFn: 'basic',
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'paytrail', label: meta?.paymentMethod?.['paytrail'] ?? '' },
					{ value: 'invoice', label: meta?.paymentMethod?.['invoice'] ?? '' },
					{ value: 'paytrailAutomaticPayment', label: meta?.paymentMethod?.['paytrailAutomaticPayment'] ?? '' },
					{ value: 'invoiceAutomaticPayment', label: meta?.paymentMethod?.['invoiceAutomaticPayment'] ?? '' },
				] as Array<{ value: PaymentMethod; label: string }>,
			},
			cell: ({ row }) => {
				return <Text>{getPaymentMethodTranslation(row.original.paymentMethod)}</Text>;
			},
		}),
		columnHelper.accessor('orderStatus', {
			header: t('general.order_status'),
			sortingFn: 'basic',
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'completed', label: meta?.orderStatus?.['completed'] ?? '' },
					{ value: 'inProgress', label: meta?.orderStatus?.['inProgress'] ?? '' },
					{ value: 'cancelled', label: meta?.orderStatus?.['cancelled'] ?? '' },
					{ value: 'draft', label: meta?.orderStatus?.['draft'] ?? '' },
				] as Array<{ value: OrderStatus; label: string }>,
			},
			cell: ({ row }) => {
				return (
					<Badge backgroundColor={getPaymentStatusBadgeColor(row.original.orderStatus)}>{meta?.orderStatus?.[row.original.orderStatus] || t('general.unknown')}</Badge>
				);
			},
		}),
		columnHelper.accessor('paymentStatus', {
			header: t('general.payment_status'),
			sortingFn: 'basic',
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'awaitingPayment', label: meta?.paymentStatus?.['awaitingPayment'] ?? '' },
					{ value: 'awaitingInvoiceCreation', label: meta?.paymentStatus?.['awaitingInvoiceCreation'] ?? '' },
					{ value: 'cancelled', label: meta?.paymentStatus?.['cancelled'] ?? '' },
					{ value: 'paid', label: meta?.paymentStatus?.['paid'] ?? '' },
				] as Array<{ value: PaymentStatus; label: string }>,
			},
			cell: ({ row }) => {
				return (
					<Badge backgroundColor={getPaymentStatusBadgeColor(row.original.paymentStatus)}>
						{meta?.paymentStatus?.[row.original.paymentStatus] || t('general.unknown')}
					</Badge>
				);
			},
		}),
		columnHelper.accessor('discountCodeID', {
			header: t('general.discount_code'),
			enableSorting: false,
			cell: ({ row }) => {
				if (!row.original.discountCodeID) {
					return '-';
				}
				const discountCodeName = discountCodes?.data.discountCodes.find(d => d.discountCodeID === row.original.discountCodeID)?.name;
				return <Text>{discountCodeName || t('general.code_has_been_deleted')}</Text>;
			},
		}),
		columnHelper.accessor('totalWithoutVat', {
			header: t('general.total_without_vat'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.totalWithoutVat) {
					return '-';
				}
				return <Text>{priceFormatter(row.original.totalWithoutVat)}</Text>;
			},
		}),

		columnHelper.accessor('totalWithVat', {
			header: t('general.total'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.totalWithVat) {
					return '-';
				}
				return <Text>{priceFormatter(row.original.totalWithVat)}</Text>;
			},
		}),
	];

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.orders')}</Heading>
					</Card.Body>
				</Card.Root>
			) : null}
			<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
				<Flex display={'flex'} flex={1} overflow={'auto'}>
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								dataKey="orders"
								keyAttribute="orderID"
								emptyDataText={t('general.no_orders_found')}
								fetchFn={(...args) => ordersService.getAllResources(...args)}
								queryParams={{ getRelated: 'orderRows' }}
								initialItemsPerPage={25}
								columns={columns}
								defaultHiddenColumns={[]}
								isServerSide={true}
								isSelectable={false}
								defaultSort={{ id: 'timeCreated', desc: true }}
								onClickRow={row => {
									openDrawer({
										id: 'accountDrawer',
										content: <OrderDrawer orderID={row.original.orderID} />,
									});
								}}
							>
								<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
									<DataCollectionTableHeader />
								</Flex>
								<Box flex={1} overflow="auto" minH={0}>
									<DataCollectionTable />
								</Box>
								<Flex gap={2} flexShrink={0}>
									<DataCollectionTableFooter />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}
